import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {useLocation} from 'react-router-dom';

interface SidePanelPanierContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const SidePanelPanierContext = createContext<
  SidePanelPanierContextType | undefined
>(undefined);

/**
 * Provider pour l'état ouvert/fermé du side panel panier (desktop uniquement).
 *
 * Ce contexte est nécessaire car le bouton panier (CartButton, dans Navbar)
 * et le panel (SidePanelPanier, dans Layout) sont dans des branches
 * différentes de l'arbre React — ils ne peuvent pas partager un useState local.
 *
 * Le provider est placé dans ClientApp, ancêtre commun de Navbar et Layout.
 */
export const SidePanelPanierProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);
  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Quand on arrive sur une page panier, le panel se ferme : la page affiche déjà
  // le panier. À garder dans le provider.
  const {pathname} = useLocation();
  useEffect(() => {
    if (pathname.includes('/panier')) {
      close();
    }
  }, [pathname, close]);

  return (
    <SidePanelPanierContext.Provider value={{isOpen, open, close, toggle}}>
      {children}
    </SidePanelPanierContext.Provider>
  );
};

const noop = (): void => undefined;

const NOOP_CONTEXT: SidePanelPanierContextType = {
  isOpen: false,
  open: noop,
  close: noop,
  toggle: noop,
};

/**
 * Hook pour contrôler le side panel panier depuis n'importe quel composant enfant de ClientApp.
 *
 * Fallback no-op si pas de provider : certains composants partagés (ex: SelectUnite via
 * useBuyingBlocks) sont rendus à la fois sous ClientApp et dans le backoffice (qui n'a
 * pas le provider). Dans ce cas, `isOpen` reste `false` et les actions sont des no-op.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useSidePanelPanier = () => {
  return useContext(SidePanelPanierContext) ?? NOOP_CONTEXT;
};
